import React, { PureComponent } from "react";
import classNames from "classnames";
import {
  SIZES,
  SHOULD_USE_RESET_PATTERN,
  VECTOR_IMAGE_ICON,
  VECTOR_IMAGE_BRAND,
  VECTOR_IMAGE_PAYMENT,
  VECTOR_IMAGE_GRAPHIC,
  VECTOR_IMAGE_ILLUSTRATION,
} from "./constants";

import styles from "./VectorImage.module.scss";
import { COLORS } from "@eventbrite/valkyrie-eds-styles/color";

export interface VectorImageProps {
  /**
   * The SVG of icon that you want to display
   * (or a component that renders and <svg />)
   */
  svg: React.ReactNode;
  /**
   * The type of icon that you want to display e.g. icon, brand, payment, graphic
   */
  type:
    | typeof VECTOR_IMAGE_ICON
    | typeof VECTOR_IMAGE_BRAND
    | typeof VECTOR_IMAGE_PAYMENT
    | typeof VECTOR_IMAGE_GRAPHIC
    | typeof VECTOR_IMAGE_ILLUSTRATION;
  /**
   * Additional content to display next to svg
   */
  children?: React.ReactNode;
  /**
   * The size of the icon
   */
  size?: (typeof SIZES)[number]; // TODO: this resolves to a string because SIZES is a computed array
  /**
   * The specified width of the icon
   */
  width?: string;
  /**
   * The specified height of the icon
   */
  height?: string;
  /**
   * The display the icon as a block level element
   */
  isBlockLevel?: boolean;
  /**
   * The fill color of the icon
   */
  color?: (typeof COLORS)[number]; // TODO: this resolves to a string because COLORS is a computed array
  /**
   * The stroke color of the icon
   */
  strokeColor?: (typeof COLORS)[number]; // TODO: this resolves to a string because COLORS is a computed array
  /**
   * Option to include a halo for the icon
   */
  hasHalo?: boolean;
  /**
   * Option to include a background color halo for the icon
   */
  haloColor?: (typeof COLORS)[number]; // TODO: this resolves to a string because COLORS is a computed array;
  /**
   * Option to override the halo size.
   */
  haloSize?: (typeof SIZES)[number]; // TODO: this resolves to a string because SIZES is a computed array;
  /**
   * Option to animate the icon
   * (Note: not all icons can be animated)
   */
  animated?: boolean;
  /**
   * Add a title attribute to the to icons markup
   */
  title?: string | String;
  __containerClassName?: string;
}

export class VectorImage extends PureComponent<VectorImageProps> {
  render() {
    const {
      svg,
      type,
      size,
      width,
      height,
      color,
      strokeColor,
      hasHalo,
      haloColor,
      haloSize,
      animated,
      isBlockLevel,
      title,
      __containerClassName,
      children,
    } = this.props;
    const className = classNames(
      styles.root,
      {
        [styles[`${type}-${size}`] as string]: size,
        [styles[`${type}-animated`] as string]: animated,
        [styles[`color-${color}`] as string]: color,
        [styles[`stroke-${strokeColor}`] as string]: strokeColor,
        [styles.block as string]: isBlockLevel,
        [styles.sizeReset as string]:
          SHOULD_USE_RESET_PATTERN.test(type) || (width && height) || haloSize,
        [styles.center as string]: haloSize,
        [styles.haloSizeOverride as string]: haloSize,
      },
      __containerClassName,
    );
    let hiddenText;
    let accessibilityProps = {};
    let style;

    // NOTE: According to Alli we may want to always have a `title` for accessibility
    // reasons, so it'd be nice if we had a mapping of types to display names that
    // we can fallback to if the `title` isn't specified
    if (title) {
      hiddenText = <span className={styles.hiddenAccessible}>{title}</span>;
    } else {
      accessibilityProps = { ...accessibilityProps, "aria-hidden": true };
    }

    if (height && width) {
      style = {
        height,
        width,
      };
    }

    const icon = (
      <i
        className={className}
        title={title?.toString()}
        data-spec="icon"
        data-testid="icon"
        style={style}
        {...accessibilityProps}
      >
        {svg}
        {hiddenText}
        {children}
      </i>
    );

    if (hasHalo) {
      let newHaloSize = size;

      if (haloSize) {
        newHaloSize = haloSize;
      }

      const haloClassName = classNames(
        [styles[`${type}-halo`]],
        {
          [styles[`${type}-halo-${newHaloSize}`] as string]: newHaloSize,
          [`eds-bg-color--${haloColor}`]: haloColor, // TODO: Replace with CSS custom property
        },
        __containerClassName,
      );

      return <span className={haloClassName}>{icon}</span>;
    }

    return icon;
  }
}
